import React from 'react';
import { View, Text, StyleSheet, Pressable, Image } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { AttendanceRecord } from './utils';

interface AttendanceItemProps {
    user: any;
    date: string;
    attendance: AttendanceRecord;
    isCoach: boolean;
    isFutureDate?: boolean;
    onToggleAttendance: (userId: string, date: string, type: 'checkin' | 'checkout') => void;
    onCommentPress: (userId: string, date: string) => void;
    onPhotoPress: (userId: string, date: string) => void;
}

const AttendanceItem: React.FC<AttendanceItemProps> = ({
    user,
    date,
    attendance,
    isCoach,
    isFutureDate = false,
    onToggleAttendance,
    onCommentPress,
    onPhotoPress,
}) => {
    const canEditAttendance = isCoach && !isFutureDate;
    const checkInTime = attendance?.checkInTime?.toDate
        ? attendance.checkInTime.toDate().toLocaleTimeString()
        : (attendance?.checkInTime ? new Date(attendance.checkInTime).toLocaleTimeString() : '');
    const checkOutTime = attendance?.checkOutTime?.toDate
        ? attendance.checkOutTime.toDate().toLocaleTimeString()
        : (attendance?.checkOutTime ? new Date(attendance.checkOutTime).toLocaleTimeString() : '');

    return (
        <View style={styles.attendanceItem}>
            <View style={styles.attendanceUserInfo}>
                {user.profileImage ? (
                    <Image
                        source={{ uri: user.profileImage }}
                        style={styles.userAvatarImage}
                    />
                ) : (
                    <View style={styles.userAvatar}>
                        <MaterialCommunityIcons name="account-child" size={20} color="#666" />
                    </View>
                )}
                <View style={styles.userDetails}>
                    <Text style={styles.attendanceUserName}>
                        {user.childName || user.userName || 'Unknown Camper'}
                    </Text>
                    <Text style={styles.attendanceUserEmail}>
                        {user.age ? `Age: ${user.age}` : ''} {user.parentName ? `• Parent: ${user.parentName}` : ''}
                    </Text>
                </View>
            </View>
            
            <View style={styles.attendanceActions}>
                <View style={styles.inOutContainer}>
                    <Pressable
                        style={[
                            styles.controlButton,
                            attendance?.checkedIn && styles.inActive,
                            !canEditAttendance && styles.disabledCheckBox,
                        ]}
                        onPress={canEditAttendance ? () => onToggleAttendance(user.userId, date, 'checkin') : undefined}
                        disabled={!canEditAttendance}
                    >
                        <View style={styles.controlButtonContent}>
                            <MaterialCommunityIcons name="login" size={16} color={attendance?.checkedIn ? '#047857' : '#374151'} />
                            <Text style={[styles.controlLabel, attendance?.checkedIn && styles.inOutLabelActive]}>Check In</Text>
                        </View>
                    </Pressable>
                    <Text style={styles.timeText}>{checkInTime}</Text>
                </View>
                <View style={styles.inOutContainer}>
                    <Pressable
                        style={[
                            styles.controlButton,
                            attendance?.checkedOut && styles.outActive,
                            !canEditAttendance && styles.disabledCheckBox,
                        ]}
                        onPress={canEditAttendance ? () => onToggleAttendance(user.userId, date, 'checkout') : undefined}
                        disabled={!canEditAttendance}
                    >
                        <View style={styles.controlButtonContent}>
                            <MaterialCommunityIcons name="logout" size={16} color={attendance?.checkedOut ? '#4338CA' : '#374151'} />
                            <Text style={[styles.controlLabel, attendance?.checkedOut && styles.inOutLabelActive]}>Check Out</Text>
                        </View>
                    </Pressable>
                    <Text style={styles.timeText}>{checkOutTime}</Text>
                </View>

                <View style={styles.actionButtons}>
                    <Pressable 
                        style={styles.actionButton} 
                        onPress={() => onCommentPress(user.userId, date)}
                    >
                        <MaterialCommunityIcons 
                            name="comment-text-outline" 
                            size={16} 
                            color="#666" 
                        />
                    </Pressable>
                    <Pressable 
                        style={styles.actionButton} 
                        onPress={() => onPhotoPress(user.userId, date)}
                    >
                        <MaterialCommunityIcons 
                            name="camera-outline" 
                            size={16} 
                            color="#666" 
                        />
                    </Pressable>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    attendanceItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: '#e9ecef',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
    },
    attendanceUserInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    userAvatar: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#f0f0f0',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    userAvatarImage: {
        width: 40,
        height: 40,
        borderRadius: 20,
        marginRight: 12,
        borderWidth: 1,
        borderColor: '#E0E0E0',
    },
    userDetails: {
        flex: 1,
    },
    attendanceUserName: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1a1a1a',
    },
    attendanceUserEmail: {
        fontSize: 12,
        color: '#999',
        marginTop: 2,
    },
    attendanceActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    disabledCheckBox: {
        opacity: 0.5,
    },
    inOutContainer: {
        alignItems: 'center',
        minWidth: 112,
        gap: 4,
    },
    controlButton: {
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 999,
        backgroundColor: '#F9FAFB',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 102,
    },
    controlButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    controlLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#374151',
    },
    inActive: {
        backgroundColor: '#ECFDF5',
        borderColor: '#A7F3D0',
    },
    outActive: {
        backgroundColor: '#EEF2FF',
        borderColor: '#C7D2FE',
    },
    inOutLabelActive: {
        color: '#111827',
    },
    timeText: {
        fontSize: 10,
        color: '#6B7280',
        minHeight: 14,
    },
    actionButtons: {
        flexDirection: 'row',
        gap: 8,
    },
    actionButton: {
        padding: 8,
        borderRadius: 6,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
});

export default AttendanceItem;

